/**
 * @module mail
 * @description Email service with Nodemailer, template rendering, and queue
 */

import type { ModuleManifest, ModuleContext, LoggerReporter } from '@gzl10/nexus-sdk'
import { mailConfigEntity, mailSendAction, mailTestAction, mailLogEntity, mailOverviewEntity } from './mail.entity.js'
import { initMailService } from './mail.service.js'
import type { MailConfig } from './mail.types.js'

export { MailService, getMailService } from './mail.service.js'
export type {
  MailConfig,
  MailConfigRow,
  MailAction,
  SendMailInput,
  SendMailOptions,
  SendMailResult
} from './mail.types.js'

export const mailModule: ModuleManifest = {
  name: 'mail',
  label: { en: 'Mail', es: 'Correo' },
  icon: 'mdi:email-outline',
  description: { en: 'Email sending and logging', es: 'Envío y registro de correos electrónicos' },
  type: 'core',
  category: 'messaging',
  dependencies: ['logger'],

  definitions: [
    mailOverviewEntity,
    mailConfigEntity,
    mailLogEntity
  ],

  actions: [
    mailSendAction,
    mailTestAction
  ],

  routePrefix: '/mail',

  init: async (ctx: ModuleContext) => {
    // Read config from SingleService (envMapping applied automatically)
    const configService = ctx.services.get<{ get: () => Promise<Record<string, unknown> | null> }>('mail_config')
    const row = await configService.get()

    const config: MailConfig = {
      host: (row?.['host'] as string) ?? 'localhost',
      port: (row?.['port'] as number) ?? 587,
      secure: row?.['secure'] === true || row?.['secure'] === 1,
      from: (row?.['from'] as string) ?? 'noreply@localhost',
      auth: row?.['auth_user']
        ? { user: row['auth_user'] as string, pass: row['auth_pass'] as string }
        : undefined
    }

    const loggerService = ctx.services.getOptional<LoggerReporter>('logger')
    const mailService = initMailService(config, ctx.core.logger, loggerService, { libPath: ctx.core.getLibPath() })
    ctx.services.register('mail', mailService)
    ctx.core.logger.debug('Mail service registered')
  }
}
